import React from "react";
import { Link } from "react-router-dom";
import Footer from "../components/Footer";
import { ScopePieChart, ScopeTimeSeriesChart } from "../components/ScopeEmissionsCharts";
import Seo from "../components/Seo";
import { esgCapabilities, proofPoints } from "../data/siteContent";
import "../styles/domain-pages.css";

const sustainabilityOutcomes = [
  {
    title: "Unified Compliance Ingestion",
    copy: "Consolidate utility bills, fuel registers, supplier invoices, logistics records and operational footprints into a governed repository.",
  },
  {
    title: "Audit-Ready Lineage for Regulators",
    copy: "Map reported metrics back to source ingestion points so audit reviewers can trace how every figure was calculated.",
  },
  {
    title: "Automated NGER & Scope Engineering",
    copy: "Replace manual extraction with transformation models for NGER, NPI, Scope 1, Scope 2 and Scope 3 reporting inputs.",
  },
  {
    title: "De-Risked Spreadsheet Dependencies",
    copy: "Move fragile Excel and SharePoint-based workflows into controlled cloud datasets with validation, ownership and repeatable processing.",
  },
  {
    title: "Enterprise Platform Readiness",
    copy: "Establish cloud foundations that support Microsoft Sustainability Manager, Cloud for Sustainability and Power BI reporting layers.",
  },
  {
    title: "Continuous Disclosure Velocity",
    copy: "Shift from annual reporting scrambles to more regular sustainability tracking across assets, suppliers, sites and business units.",
  },
];

const sustainabilityIntegrityControls = [
  "Source-to-metric lineage",
  "NGER and Scope input mapping",
  "Supplier data validation",
  "Register hardening",
  "Emissions factor controls",
  "Audit evidence packs",
];

export default function ESGSustainabilityData() {
  return (
    <div className="site-page">
      <Seo
        title="Auditable Sustainability Reporting Data Foundations | Data Apparatus"
        description="Sustainability reporting data foundations for audit-ready NGER, NPI, Scope 1, Scope 2, Scope 3, Microsoft Sustainability Manager, supplier data and Power BI reporting."
      />

      <section className="page-hero esg">
        <div className="page-hero-copy">
          <span className="eyebrow">Sustainability reporting domain expertise</span>
          <h1>Auditable foundations for corporate sustainability reporting.</h1>
          <p>
            Turn fragmented utility, procurement, supplier, finance and operational records
            into governance-grade data assets that can withstand strict regulatory scrutiny.
          </p>
          <div className="hero-actions">
            <Link to="/contact" className="primary-btn">
              Discuss sustainability reporting
            </Link>
            <Link to="/services/data-engineering" className="secondary-btn">
              View data engineering services
            </Link>
          </div>
        </div>
      </section>

      <section className="page-section">
        <div className="content-split">
          <div>
            <span className="eyebrow">The problem</span>
            <h2>Sustainability disclosure gets risky when source data relies on fragmented files.</h2>
            <p>
              When carbon and operational footprints are scattered across supplier invoices,
              local spreadsheets, detached ERP modules and manual registers, reporting errors
              become inevitable. We replace fragile workflows with automated, defensible
              data pipelines designed to reduce compliance and greenwashing risk.
            </p>
          </div>
          <div className="capability-list">
            {esgCapabilities.map((capability) => (
              <div className="capability-card-item" key={capability.title}>
                <h3>{capability.title}</h3>
                <p>{capability.copy}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="feature-block-section">
        <div className="feature-block chart-feature">
          <div className="feature-copy">
            <span className="eyebrow">01 / Audit-defense lineage</span>
            <h2>Make reported sustainability metrics traceable back to source ingestion.</h2>
            <p>
              Connect utility, supplier, procurement, finance, energy and operational sources
              into governed datasets with lineage, validation checks and reporting-ready models.
            </p>
          </div>
          <ScopePieChart />
        </div>

        <div className="feature-block reverse chart-feature">
          <div className="feature-copy">
            <span className="eyebrow">02 / Spreadsheet risk reduction</span>
            <h2>Replace fragile sustainability registers with structured operating data.</h2>
            <p>
              Bring Excel, SharePoint, ERP, supplier, construction and energy records into
              controlled models that support repeatable sustainability reporting and audit review.
            </p>
          </div>
          <ScopeTimeSeriesChart />
        </div>
      </section>

      <section className="page-section alt">
        <div className="section-heading">
          <span className="eyebrow">Business value</span>
          <h2>What sustainability, finance and risk teams get.</h2>
        </div>
        <div className="outcome-grid">
          {sustainabilityOutcomes.map((outcome) => (
            <div className="outcome-card narrative-card" key={outcome.title}>
              <h3>{outcome.title}</h3>
              <p>{outcome.copy}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="page-section sustainability-integrity-section">
        <div className="sustainability-integrity-inner">
          <div className="section-heading left">
            <span className="eyebrow">Relevant experience</span>
            <h2>Practical sustainability delivery where reporting quality has governance consequences.</h2>
            <p>
              This capability is based on real sustainability data architecture, modelling,
              analytics and reporting delivery across compliance-heavy environments where
              traceability and repeatability matter.
            </p>
          </div>

          <div className="sustainability-integrity-grid">
            {sustainabilityIntegrityControls.map((control) => (
              <div key={control}>{control}</div>
            ))}
          </div>

          <div className="sustainability-evidence-strip">
            <span className="eyebrow">Relevant delivery evidence</span>
            {proofPoints.slice(2).map((point) => (
              <div key={point}>{point}</div>
            ))}
          </div>
        </div>
      </section>

      <section className="cta-band">
        <h2>Need sustainability data that can stand up to review?</h2>
        <p>Start with a review of source systems, reporting obligations, lineage gaps and manual workarounds.</p>
        <Link to="/contact" className="primary-btn">
          Book a sustainability data review
        </Link>
      </section>

      <Footer />
    </div>
  );
}
